import { Trans, useLingui } from '@lingui/react/macro'
import { CantileverButton } from 'components/CantileverButton'
import { Button } from 'designSystem/components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'designSystem/components/Dialog'
import { Input } from 'designSystem/components/Input'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { Save } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

const SaveButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

const UnsavedDot = styled.div`
  position: absolute;
  top: 0.125rem;
  right: 0.125rem;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: var(--primary);
  pointer-events: none;
`

const UnsavedAnnotationLight = styled.span`
  align-self: center;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: var(--destructive);
`

interface ChartSaveActionProps {
  appearance?: 'hud' | 'toolbar'
}

export const ChartSaveAction: React.FC<ChartSaveActionProps> = ({
  appearance = 'hud',
}) => {
  const { t } = useLingui()

  const {
    saveChart,
    hasUnsavedChanges,
    hasUnsavedAnnotationChanges,
    isNewChart,
    chartName,
    setChartName,
  } = useScatterplotContext()

  // Naming a chart is only forced on its first save. A brand-new chart opens
  // a name prompt (pre-filled with the current default); an existing chart
  // re-saves silently. Cancelling the prompt aborts the save.
  const [isNamePromptOpen, setIsNamePromptOpen] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const handleSaveClick = useCallback(() => {
    if (isNewChart) {
      setNameDraft(chartName ?? '')
      setIsNamePromptOpen(true)
      return
    }
    void saveChart()
  }, [isNewChart, chartName, saveChart])
  const handleNameConfirm = useCallback(() => {
    const name = nameDraft.trim()
    if (!name) return
    setIsNamePromptOpen(false)
    setChartName(name)
    void saveChart(name)
  }, [nameDraft, saveChart, setChartName])

  const namePrompt = (
    <Dialog
      open={isNamePromptOpen}
      onOpenChange={(open) => {
        if (!open) setIsNamePromptOpen(false)
      }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Name this view</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Give the view a name before saving so it can be found later.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          placeholder={t`View name`}
          value={nameDraft}
          onChange={(event) => setNameDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleNameConfirm()
          }}
        />
        <DialogFooter>
          <Button variant='ghost' onClick={() => setIsNamePromptOpen(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button disabled={!nameDraft.trim()} onClick={handleNameConfirm}>
            <Trans>Save view</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  if (appearance === 'toolbar') {
    return (
      <>
        <ChartTooltip label={t`Save view`} side='bottom'>
          <Button
            variant='primaryRaised'
            size='icon-lg'
            aria-label={t`Save view`}
            disabled={!hasUnsavedChanges}
            onClick={handleSaveClick}>
            <Save size={18} strokeWidth={1.5} />
          </Button>
        </ChartTooltip>
        {namePrompt}
      </>
    )
  }

  return (
    <>
      {hasUnsavedAnnotationChanges && (
        <ChartTooltip label={t`Unsaved annotation changes`} side='top'>
          <UnsavedAnnotationLight
            role='status'
            aria-label={t`Unsaved annotation changes`}
          />
        </ChartTooltip>
      )}
      <SaveButtonWrapper>
        <ChartTooltip
          label={t`Save`}
          side='top'
          style={{ display: 'inline-flex' }}>
          <CantileverButton
            iconOnly
            ghost
            size='small'
            onClick={handleSaveClick}>
            <Save size={16} />
          </CantileverButton>
        </ChartTooltip>
        {hasUnsavedChanges && <UnsavedDot />}
      </SaveButtonWrapper>
      {namePrompt}
    </>
  )
}
